import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { DataNode } from 'components/WorkspaceEditor/types'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback, useMemo } from 'react'
import {
    useNodeByContentId,
    useNodeMapShallowSelector,
} from '../store/NodeMapStore'
import {
    findFirstFocusableContent,
    isArrowDownShortcut,
    isArrowUpShortcut,
    isNotFocusableNode,
    isStructuralContentNodeWithPeriod,
} from '../utils'
import { handleArrowNavigation } from '../utils/focusNavigation.utils'

interface FocusableContentResult {
    contentItem: Partial<TextContentElement> | null
    nodeKey?: string
    isArticle?: boolean
}

interface FocusableContentResult {
    contentItem: Partial<TextContentElement> | null
    nodeKey?: string
    isArticle?: boolean
}

/**
 * Custom hook for node navigation utilities.
 * Provides functions for selecting and assigning target nodes during navigation.
 * Handles keyboard events to navigate through nodes based on contentItem.id.
 * @param contentItemId - The content item.id to navigate to.
 * @return Object containing functions to get focusable content items and handle node navigation.
 * @example
 * const { getFocusableContentItem } = useNodeNavigation(contentItemId)
 * getFocusableContentItem(event) // Returns the focusable content item based on arrow key direction
 */
export const useNodeNavigation = (contentItemId: string | undefined) => {
    // Hooks must run unconditionally to keep hook order stable
    const { contentItemToNodeMap } = useNodeMapShallowSelector((state) => ({
        contentItemToNodeMap: state.contentItemToNodeMap,
    }))

    const parentNode = useNodeByContentId(contentItemId ?? '')

    // Pre-filter visible nodes for optimal performance
    const visibleNodes = useMemo(() => {
        const seenKeys = new Set<string>()
        return Array.from(contentItemToNodeMap.values()).filter((node) => {
            if (seenKeys.has(node.key) || node.isHidden) return false
            seenKeys.add(node.key)

            // Filter out nodes that have no focusable content items
            const contentItems = node.contentItemNodes
            if (contentItems?.length) {
                // Filter out structural nodes with [unfocusable data, '.'] pattern
                if (isStructuralContentNodeWithPeriod(contentItems)) {
                    return false
                }

                const hasFocusableContent = contentItems.some(
                    (item) => !isNotFocusableNode(item, false)
                )
                if (!hasFocusableContent) {
                    return false
                }
            }

            return true
        })
    }, [contentItemToNodeMap])

    // Simplified navigation using pre-filtered visible nodes
    const getTargetNode = useCallback(
        (
            event: React.KeyboardEvent<HTMLElement>
        ): {
            node: DataNode | null
            currentIndex: number
            operation: 'previous' | 'next' | null
        } => {
            if (!contentItemId)
                return { node: null, currentIndex: -1, operation: null }

            const currentContent = contentItemToNodeMap.get(contentItemId)

            if (!currentContent)
                return { node: null, currentIndex: -1, operation: null }

            const currentIndex = visibleNodes.findIndex(
                (node) => node.key === currentContent.key
            )

            if (currentIndex === -1)
                return { node: null, currentIndex: -1, operation: null }

            if (isArrowUpShortcut(event) && currentIndex > 0) {
                return (
                    handleArrowNavigation(
                        'up',
                        currentIndex,
                        currentContent,
                        visibleNodes,
                        NODE_TYPES
                    ) || { node: null, currentIndex, operation: null }
                )
            }

            if (
                isArrowDownShortcut(event) &&
                currentIndex < visibleNodes.length - 1
            ) {
                return (
                    handleArrowNavigation(
                        'down',
                        currentIndex,
                        currentContent,
                        visibleNodes,
                        NODE_TYPES
                    ) || { node: null, currentIndex, operation: null }
                )
            }

            return { node: null, currentIndex, operation: null }
        },
        [visibleNodes, contentItemToNodeMap, contentItemId]
    )

    /**
     * Search for focusable content in subsequent visible nodes
     * Simplified since visibleNodes now only contains nodes with focusable content
     */
    const findFocusableInSubsequentNodes = useCallback(
        (
            currentIndex: number,
            operation: 'previous' | 'next'
        ): Partial<TextContentElement> | null => {
            const searchNodes =
                operation === 'previous'
                    ? visibleNodes.slice(0, currentIndex).reverse()
                    : visibleNodes.slice(currentIndex + 1)

            for (const node of searchNodes) {
                if (node?.contentItemNodes?.length) {
                    // Since visibleNodes are pre-filtered to have focusable content,
                    // we can use the first focusable content more confidently
                    const focusableItem = findFirstFocusableContent(
                        node.contentItemNodes
                    )
                    if (focusableItem) return focusableItem
                }
            }
            return null
        },
        [visibleNodes]
    )

    /**
     * Determines the focusable content item from a target node
     * Optimized since visibleNodes are pre-filtered to contain focusable content
     */
    const getFocusableContentItemNode = useCallback(
        (
            targetNode: DataNode,
            currentIndex: number,
            operation: 'previous' | 'next' | null = null
        ): Partial<TextContentElement> | null => {
            const contentItemNodes = targetNode.contentItemNodes
            if (!contentItemNodes?.length) {
                return null
            }

            // Handle single node case
            // Since visibleNodes are pre-filtered, single content items should be focusable
            if (contentItemNodes.length === 1) {
                return contentItemNodes[0]
            }

            // Handle special case: OptionGroup/Hyperlink followed by period
            const isStructuralWithPeriod = isStructuralContentNodeWithPeriod(
                contentItemNodes
            )

            if (isStructuralWithPeriod) {
                // Check if the period text is focusable (has actual text content)
                const periodText = contentItemNodes[1] // The "." text element
                const hasFocusableText =
                    periodText?.text && periodText.text.trim().length > 0

                if (hasFocusableText) {
                    // Focus on the period text within this node
                    return periodText
                } else {
                    // Search in subsequent nodes (original behavior)
                    const result = operation
                        ? findFocusableInSubsequentNodes(
                              currentIndex,
                              operation
                          )
                        : null
                    return result
                }
            }

            // Default: find first focusable node in mixed content
            // Still needed because nodes with mixed focusable/non-focusable content are kept
            const result =
                contentItemNodes.find((node) => !isNotFocusableNode(node)) ||
                null
            return result
        },
        [findFocusableInSubsequentNodes]
    )

    /**
     * Retrieves a focusable content item based on the arrow key direction in the event.
     * Optimized version using pre-filtered visible nodes for better performance.
     * @param event - The keyboard event triggering the operation (ArrowUp/ArrowDown).
     * @returns An object containing the focusable content item and its node key.
     */
    const getFocusableContentItem = useCallback(
        (event: React.KeyboardEvent<HTMLElement>): FocusableContentResult => {
            if (!parentNode || visibleNodes.length === 0) {
                return { contentItem: null, nodeKey: undefined }
            }

            const { node: targetNode, currentIndex, operation } = getTargetNode(
                event
            )
            if (!targetNode?.contentItemNodes?.length) {
                return { contentItem: null, nodeKey: undefined }
            }

            // Check if this is an article node
            const isArticleNode = targetNode.type === NODE_TYPES.Article

            const focusableContentItem =
                getFocusableContentItemNode(
                    targetNode,
                    currentIndex,
                    operation
                ) || findFirstFocusableContent(targetNode.contentItemNodes)

            if (!focusableContentItem)
                return { contentItem: null, nodeKey: undefined }

            return {
                contentItem: focusableContentItem,
                nodeKey: targetNode.key,
                isArticle: isArticleNode,
            }
        },
        [parentNode, visibleNodes, getTargetNode, getFocusableContentItemNode]
    )

    return {
        getFocusableContentItem,
    }
}
